import React, { useEffect, useState } from "react";
import { db } from "../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import ReferralCoupons from "../components/ReferralCoupons";
import MyCoupons from "../components/MyCoupons";

const Profile = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(user?.displayName || "");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [updating, setUpdating] = useState(false);
  const [customOrders, setCustomOrders] = useState([]);
  const [pastSubscriptions, setPastSubscriptions] = useState([]);

  // Generate referral link
  const referralLink = `${window.location.origin}/login?ref=${user?.uid}`;

  useEffect(() => {
    const fetchActivePlan = async () => {
      try {
        const q = query(
          collection(db, "orders", user.uid, "subscriptions"),
          where("status", "==", "active")
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setPlan(data);
          setPhone(data.phone || "");
          setAddress(data.address || "");
        }
      } catch (error) {
        console.error("Error fetching plan:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivePlan();
  }, [user.uid]);

  useEffect(() => {
    const fetchCustomOrders = async () => {
      if (!user?.uid) return;
      try {
        const q = query(collection(db, "custom_orders"), where("userId", "==", user.uid));
        const snap = await getDocs(q);
        setCustomOrders(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setCustomOrders([]);
      }
    };
    fetchCustomOrders();
  }, [user]);

  // Fetch past subscriptions
  useEffect(() => {
    const fetchPastSubscriptions = async () => {
      if (!user?.uid) return;
      try {
        const q = query(collection(db, "subscriptions"), where("userId", "==", user.uid));
        const snapshot = await getDocs(q);
        const subscriptions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPastSubscriptions(subscriptions);
      } catch (error) {
        console.error("Error fetching past subscriptions:", error);
        setPastSubscriptions([]);
      }
    };
    fetchPastSubscriptions();
  }, [user]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const q = query(
        collection(db, "orders", user.uid, "subscriptions"),
        where("status", "==", "active")
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docRef = snapshot.docs[0].ref;
        await updateDoc(docRef, {
          userName: name,
          phone,
          addressDetails: address,
        });
        alert("✅ Profile updated successfully.");
      }
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert("Failed to update profile.");
    }
    setUpdating(false);
  };

  if (loading) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <Header user={user} />
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <Header user={user} />
      <h1 className="text-2xl font-bold text-green-700 mb-6">👤 Welcome to Your Profile!</h1>

      <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
          📋 Account Info
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={user.email}
              readOnly
              className="w-full mt-1 border rounded px-3 py-2 bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full mt-1 border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full mt-1 border rounded px-3 py-2"
              rows={3}
            />
          </div>
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:opacity-50"
          >
            {updating ? "Updating..." : "Update Profile"}
          </button>
        </div>
      </div>

      {/* Referral Section */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
          🎁 Refer and Earn ₹150
        </h2>
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Share this link with your friends. When they subscribe, you'll get ₹150 off on your next plan!
          </p>
          <div className="bg-gray-50 border p-3 rounded-lg">
            <div className="flex items-center justify-between">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-800 font-mono"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(referralLink);
                  alert("✅ Referral link copied to clipboard!");
                }}
                className="ml-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
              >
                Copy
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            💡 Your friends will get a special welcome offer when they sign up using your link!
          </p>
        </div>
      </div>

      {/* My Coupons */}
      <MyCoupons userId={user?.uid} />

      {/* Referral Coupons */}
      <ReferralCoupons userId={user?.uid} />

      {/* Past Subscriptions */}
      {pastSubscriptions.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
            📋 Past Subscriptions ({pastSubscriptions.length})
          </h2>
          <div className="space-y-3">
            {pastSubscriptions.map((subscription) => (
              <div key={subscription.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">
                      {subscription.planName || "Unknown Plan"}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {subscription.rotisPerDay || 0} rotis/day • {subscription.deliverySlot || "Morning"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {subscription.startDate && (
                        <>
                          Started: {new Date(subscription.startDate).toLocaleDateString()}
                          {subscription.endDate && (
                            <> • Ended: {new Date(subscription.endDate).toLocaleDateString()}</>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      subscription.status === "completed" 
                        ? "bg-gray-100 text-gray-800" 
                        : subscription.status === "cancelled"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}>
                      {subscription.status === "completed" ? "Completed" : 
                       subscription.status === "cancelled" ? "Cancelled" : 
                       subscription.status || "Active"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
            📦 Active Plan
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Plan:</span>
              <span className="text-green-700 font-semibold">{plan.planName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Rotis/day:</span>
              <span className="text-green-700 font-semibold">{plan.rotisPerDay}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Delivery Slot:</span>
              <span className="text-green-700 font-semibold">{plan.deliverySlot}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Start Date:</span>
              <span className="text-green-700 font-semibold">{plan.startDate}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="font-medium text-gray-700">Address:</span>
              <span className="text-green-700 font-semibold text-right max-w-xs">{plan.address}</span>
            </div>
          </div>
        </div>
      )}

      {customOrders.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-bold mb-2 text-green-700">Your Custom Orders</h3>
          <ul className="space-y-2">
            {customOrders.map((order) => (
              <li key={order.id} className="bg-white border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <span className="font-semibold text-green-700">Qty: {order.qty}</span> &mdash; {order.address}
                </div>
                <div className="text-xs text-gray-500">{order.status} | {order.deliveryDate}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100">
        <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
          📊 Plan History
        </h2>
        <div className="text-center py-8">
          <div className="text-4xl mb-2">📈</div>
          <p className="text-gray-500">Coming soon...</p>
          <p className="text-sm text-gray-400 mt-1">We're working on bringing you detailed plan history</p>
        </div>
      </div>
    </div>
  );
};

export default Profile;
